import React from 'react';

import { faUsers } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { FormattedMessage, useIntl } from 'react-intl';

import car from 'src/assets/images/car.png';
import { getLocalUserId, PERMISSION } from 'src/features/auth';
import { useListCardsInParking } from 'src/features/cards/api/useListCardsInParking';
import { useDeleteMembershipGroupMember } from 'src/features/memberships/api/useDeleteMembershipGroupMember';
import { useListMembershipGroupCards } from 'src/features/memberships/api/useListMembershipGroupCards';
import { useListMembershipGroupMembers } from 'src/features/memberships/api/useListMembershipGroupMembers';
import { MembershipGroupDeleteFormModal } from 'src/features/memberships/components/forms/MembershipGroupDeleteFormModal';
import { MembershipGroupFormModal } from 'src/features/memberships/components/forms/MembershipGroupFormModal';
import { MembershipGroupMemberCreateFormModal } from 'src/features/memberships/components/forms/MembershipGroupMemberCreateFormModal';
import { MembershipGroupMemberEditFormModal } from 'src/features/memberships/components/forms/MembershipGroupMemberEditFormModal';
import { MembershipGroupMemberBlock } from 'src/features/memberships/components/MembershipGroupMemberBlock';
import MembershipVehicleSection from 'src/features/memberships/components/MembershipVehicleSection';
import { ALLOWED_DAYS, MEMBERSHIP_GROUP_MEMBER_ACCESS_LEVEL, VEHICLE_LAYOUT } from 'src/features/memberships/types';
import { ALL_POINTS_OPTION, END_FULL_DAY, START_FULL_DAY } from 'src/features/memberships/utils/constants';
import { membershipMessages } from 'src/features/memberships/utils/messages';
import { ICON_MAP } from 'src/features/parkingStatus/utils';
import { useListPoints } from 'src/features/points/api/useListPoints';
import { useListSubParkings } from 'src/features/subParkings/api/useListSubParkings';
import { MEMBERSHIP_ROLES } from 'src/features/userManagement/types';
import useHasPermissions from 'src/hooks/useHasPermissions';
import usePagination from 'src/hooks/usePagination';
import { useVehiclesLayout } from 'src/providers/MembershipVehiclesLayoutProvider';
import { useParking } from 'src/providers/ParkingProvider';
import {
    GeneralBlock,
    GeneralBlockBody,
    GeneralBlockHeader,
    GeneralBlockTitle,
    GeneralBlockValue,
} from 'src/registry/components/blocks/GeneralBlock';
import GeneralMessageBlock from 'src/registry/components/blocks/GeneralMessageBlock';
import {
    SectionBlock,
    SectionBlockBody,
    SectionBlockHead,
    SectionBlockHeader,
    SectionBlockSeparator,
    SectionBlockTitle,
} from 'src/registry/components/blocks/SectionBlock';
import SquareIcon from 'src/registry/components/blocks/SquareIcon';
import { toast } from 'src/registry/hooks/UseToast';
import { Badge } from 'src/registry/ui/Badge';
import { Button } from 'src/registry/ui/Button';
import {
    Dialog,
    DialogBody,
    DialogClose,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogSeparator,
    DialogTitle,
} from 'src/registry/ui/Dialog';
import { Skeleton } from 'src/registry/ui/Skeleton';
import { useTitleStore } from 'src/stores/useTitleStore';
import { cn } from 'src/utils';
import { formatTime } from 'src/utils/formatTime';
import { getFormattedDateTime } from 'src/utils/getFormattedDateTime';
import { generalMessages, generalMessagesDays } from 'src/utils/messages';

import type { MembershipGroup, MembershipGroupMember } from 'src/features/memberships/types';

interface MembershipGroupDashboardProps {
    membershipGroup?: MembershipGroup;
    isLoadingMembershipGroup?: boolean;
    refetchMembershipGroup?: () => void;
}
const MembershipGroupDashboard = ({
    membershipGroup,
    isLoadingMembershipGroup = true,
    refetchMembershipGroup,
}: MembershipGroupDashboardProps) => {
    const { formatMessage } = useIntl();
    const userId = getLocalUserId();
    const { parkingId, parkingRole } = useParking();
    const { setTitle, removeTitle } = useTitleStore();
    const permissionDetails = useHasPermissions([PERMISSION.VEHICLE_MEMBERSHIP_DETAILS_MANAGE]);
    const permissionMembers = useHasPermissions([PERMISSION.VEHICLE_MEMBERSHIP_MEMBERS_MANAGE]);
    const permissionVehicles = useHasPermissions([PERMISSION.VEHICLE_MEMBERSHIP_VEHICLES_MANAGE]);

    const [isMemberModalOpen, setIsMemberModalOpen] = React.useState(false);
    const { getVehiclesLayout } = useVehiclesLayout();
    const vehicleLayout = getVehiclesLayout(membershipGroup?.id);

    const { queryPaginationOptions, ...pagination } = usePagination();

    const [selectedMembershipGroupMember, setSelectedMembershipGroupMember] =
        React.useState<MembershipGroupMember | null>(null);
    const [selectedDeleteMembershipGroupMember, setSelectedDeleteMembershipGroupMember] =
        React.useState<MembershipGroupMember | null>(null);

    const {
        data: membershipGroupMembers,
        isLoading: isLoadingMembershipGroupMembers,
        refetch: refetchMembershipGroupMembers,
    } = useListMembershipGroupMembers({
        parkingId,
        membershipGroupId: membershipGroup?.id || -1,
        config: { enabled: !!membershipGroup },
    });

    const { data: points, isLoading: isLoadingPoints } = useListPoints({ parkingId });

    const { data: subParkings, isLoading: isLoadingSubParkings } = useListSubParkings({ parkingId });

    React.useEffect(() => {
        if (vehicleLayout === VEHICLE_LAYOUT.GRID) {
            pagination.setPaginationState({ pageIndex: 0, pageSize: 25 });
        }
    }, [vehicleLayout]);

    const {
        data: membershipGroupCards,
        isLoading: isLoadingMembershipGroupCards,
        refetch: refetchMembershipGroupCards,
    } = useListMembershipGroupCards({
        parkingId,
        membershipGroupId: membershipGroup?.id || -1,
        ...(vehicleLayout === VEHICLE_LAYOUT.TABLE ? queryPaginationOptions : {}),
        config: { enabled: !!membershipGroup },
    });

    const { mutate: deleteMembershipGroupMember } = useDeleteMembershipGroupMember({
        onSuccess: () => {
            void handleMembershipGroupUpdate();
            setSelectedDeleteMembershipGroupMember(null);
            toast({
                title: formatMessage(membershipMessages.deletedMember),
            });
        },
    });

    const { refetch: refetchInParkingCards } = useListCardsInParking({
        parkingId,
        card_number: membershipGroupCards?.results.map((card) => card.number).join(','),
        config: { enabled: !!membershipGroupCards },
    });

    const accessLevel = React.useMemo(() => {
        if (!membershipGroupMembers) return;
        const currentMembershipGroupMember = membershipGroupMembers.results.find(
            (member) => member.user?.id === userId,
        );

        return currentMembershipGroupMember?.access_level;
    }, [membershipGroupMembers, userId]);

    // Only admins can see the change member buttons
    const canSeeChangeMemberButtons = React.useMemo(() => {
        if (parkingRole !== MEMBERSHIP_ROLES.CLIENT) return permissionMembers;

        return accessLevel === MEMBERSHIP_GROUP_MEMBER_ACCESS_LEVEL.ADMIN;
    }, [accessLevel, parkingRole, permissionMembers]);

    // Only guests cannot see the change card buttons
    const canSeeChangeCardButtons = React.useMemo(() => {
        if (parkingRole !== MEMBERSHIP_ROLES.CLIENT) return permissionVehicles;

        return accessLevel === MEMBERSHIP_GROUP_MEMBER_ACCESS_LEVEL.MEMBER || canSeeChangeMemberButtons;
    }, [accessLevel, canSeeChangeMemberButtons, permissionVehicles]);

    React.useEffect(() => {
        if (!membershipGroup) return;
        setTitle(membershipGroup.name);
        return () => {
            removeTitle();
        };
    }, [membershipGroup]);

    const handleMembershipGroupUpdate = () => {
        if (refetchMembershipGroup) {
            void refetchMembershipGroup();
        }
        void refetchMembershipGroupMembers();
        void refetchMembershipGroupCards();
        void refetchInParkingCards();
    };

    // Check if all points are included in the allowed_points array but the keyword #ALL# has not been used
    const containsAllPoints = React.useMemo(() => {
        if (!membershipGroup?.allowed_points || !points?.results) return false;
        return points?.results
            .map((point) => point.id)
            .every((pointId) => membershipGroup.allowed_points.includes(pointId));
    }, [membershipGroup?.allowed_points, points?.results]);

    const containsAllSubParkings = React.useMemo(() => {
        if (!membershipGroup?.allowed_parkings || !subParkings?.results) return false;
        return subParkings?.results
            .map((subParking) => subParking.id)
            .every((subParkingId) => membershipGroup?.allowed_parkings.includes(subParkingId));
    }, [membershipGroup?.allowed_parkings, subParkings?.results]);

    const isFullDay = React.useMemo(() => {
        return (
            membershipGroup?.allowed_hours_start === START_FULL_DAY &&
            membershipGroup?.allowed_hours_end === END_FULL_DAY
        );
    }, [membershipGroup?.allowed_hours_start, membershipGroup?.allowed_hours_end]);

    const closeMemberModal = (isOpen: boolean) => {
        setSelectedMembershipGroupMember(null);
        setIsMemberModalOpen(isOpen);
    };

    const openMemberModal = (member: MembershipGroupMember) => {
        setSelectedMembershipGroupMember(member);
        setIsMemberModalOpen(true);
    };

    const getDisplayDate = (dateStr?: string) => {
        if (!dateStr) return '-';
        const formattedTime = dateStr ? formatTime(dateStr) : '';
        return `${getFormattedDateTime(dateStr).formattedDate} ${formattedTime}`;
    };

    return (
        <>
            <Dialog
                open={!!selectedDeleteMembershipGroupMember}
                onOpenChange={() => setSelectedDeleteMembershipGroupMember(null)}
            >
                <DialogContent>
                    <DialogHeader className="flex-row gap-4">
                        <SquareIcon icon={ICON_MAP.DELETE} />
                        <DialogTitle>{formatMessage(membershipMessages.removeMember)}</DialogTitle>
                    </DialogHeader>

                    <DialogSeparator />

                    <DialogBody>
                        <p className="text-gray-500">
                            {formatMessage(membershipMessages.confirmRemoveMember, {
                                name:
                                    selectedDeleteMembershipGroupMember?.user?.name ??
                                    selectedDeleteMembershipGroupMember?.invitation,
                            })}
                        </p>
                    </DialogBody>

                    <DialogSeparator />

                    <DialogFooter>
                        <DialogClose asChild>
                            <Button variant="outline" type="button" className="flex-1">
                                {formatMessage(generalMessages.cancel)}
                            </Button>
                        </DialogClose>

                        <Button
                            variant="destructiveLight"
                            onClick={() => {
                                if (selectedDeleteMembershipGroupMember && membershipGroup) {
                                    void deleteMembershipGroupMember({
                                        membershipGroupMemberId: selectedDeleteMembershipGroupMember.id,
                                        membershipGroupId: membershipGroup?.id,
                                        parkingId,
                                    });
                                }
                            }}
                            className="flex-1"
                        >
                            {formatMessage(membershipMessages.removeMember)}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {canSeeChangeMemberButtons && membershipGroup && (
                <MembershipGroupMemberEditFormModal
                    onSuccess={handleMembershipGroupUpdate}
                    parkingId={parkingId}
                    membershipGroupId={membershipGroup.id}
                    membershipGroupMember={selectedMembershipGroupMember}
                    isMemberModalOpen={isMemberModalOpen}
                    setIsMemberModalOpen={closeMemberModal}
                />
            )}
            <div className="flex flex-col gap-4 sm:gap-6 md:flex-row">
                <SectionBlock>
                    <SectionBlockHeader className="min-h-9">
                        <SectionBlockHead>
                            <SectionBlockTitle>
                                <FormattedMessage {...membershipMessages.yourMembership} />
                            </SectionBlockTitle>
                        </SectionBlockHead>
                        {permissionDetails && (
                            <>
                                <MembershipGroupFormModal
                                    selectedMembershipGroup={membershipGroup}
                                    modalMode="edit"
                                    onSubmit={handleMembershipGroupUpdate}
                                >
                                    <Button variant="outline" size="sm">
                                        <FontAwesomeIcon className="text-primary-700" icon={ICON_MAP.EDIT} />
                                        {formatMessage(generalMessages.edit)}
                                    </Button>
                                </MembershipGroupFormModal>
                                <MembershipGroupDeleteFormModal selectedMembershipGroup={membershipGroup}>
                                    <Button variant="outline" size="sm">
                                        <FontAwesomeIcon className="text-primary-700" icon={ICON_MAP.DELETE} />
                                        {formatMessage(generalMessages.delete)}
                                    </Button>
                                </MembershipGroupDeleteFormModal>

                                <MembershipGroupFormModal
                                    selectedMembershipGroup={membershipGroup}
                                    modalMode="copy"
                                    onSubmit={() => console.log('COPY ===>')}
                                >
                                    <Button variant="outline" size="sm">
                                        <FontAwesomeIcon className="text-primary-700" icon={ICON_MAP.COPY} />
                                        {formatMessage(generalMessages.copy)}
                                    </Button>
                                </MembershipGroupFormModal>
                            </>
                        )}
                    </SectionBlockHeader>
                    <SectionBlockSeparator />
                    <GeneralBlock asChild>
                        <SectionBlockBody className="xs:grid-cols-2 relative grid overflow-hidden md:grid-cols-1 lg:grid-cols-2">
                            <div className="flex flex-col">
                                <GeneralBlockHeader variant="dashboard">
                                    <GeneralBlockTitle>
                                        <FormattedMessage {...membershipMessages.validFrom} />
                                    </GeneralBlockTitle>
                                </GeneralBlockHeader>
                                <GeneralBlockBody variant="dashboard">
                                    {isLoadingMembershipGroup ? (
                                        <Skeleton className="h-8 w-full max-w-48" />
                                    ) : (
                                        <GeneralBlockValue size="small" className="break-all">
                                            {getDisplayDate(membershipGroup?.valid_from)}
                                        </GeneralBlockValue>
                                    )}
                                </GeneralBlockBody>
                            </div>
                            <div className="flex flex-col">
                                <GeneralBlockHeader variant="dashboard">
                                    <GeneralBlockTitle>
                                        <FormattedMessage {...membershipMessages.validUntil} />
                                    </GeneralBlockTitle>
                                </GeneralBlockHeader>
                                <GeneralBlockBody variant="dashboard">
                                    {isLoadingMembershipGroup ? (
                                        <Skeleton className="h-8 w-full max-w-48" />
                                    ) : (
                                        <GeneralBlockValue size="small" className="break-all">
                                            {new Date(membershipGroup?.valid_until || '').getFullYear() > 2100 ? (
                                                <FormattedMessage {...generalMessages.indefinite} />
                                            ) : (
                                                membershipGroup && getDisplayDate(membershipGroup?.valid_until)
                                            )}
                                        </GeneralBlockValue>
                                    )}
                                </GeneralBlockBody>
                            </div>
                            <div className="flex flex-col">
                                <GeneralBlockHeader variant="dashboard">
                                    <GeneralBlockTitle>
                                        <FormattedMessage {...membershipMessages.allowedHours} />
                                    </GeneralBlockTitle>
                                </GeneralBlockHeader>
                                <GeneralBlockBody variant="dashboard">
                                    {isLoadingMembershipGroup ? (
                                        <Skeleton className="h-8 w-full max-w-52" />
                                    ) : (
                                        <GeneralBlockValue size="small">
                                            {isFullDay ? (
                                                <FormattedMessage {...membershipMessages.fullDay} />
                                            ) : (
                                                `${membershipGroup?.allowed_hours_start} - ${membershipGroup?.allowed_hours_end}`
                                            )}
                                        </GeneralBlockValue>
                                    )}
                                </GeneralBlockBody>
                            </div>
                            <div className="flex flex-col">
                                <GeneralBlockHeader variant="dashboard">
                                    <GeneralBlockTitle>
                                        <FormattedMessage {...membershipMessages.allowedParkingPlaces} />
                                    </GeneralBlockTitle>
                                </GeneralBlockHeader>
                                <GeneralBlockBody variant="dashboard">
                                    {isLoadingMembershipGroup ? (
                                        <Skeleton className="h-8 w-full max-w-16" />
                                    ) : (
                                        <GeneralBlockValue size="small">
                                            {membershipGroup?.allowed_parking_places}
                                        </GeneralBlockValue>
                                    )}
                                </GeneralBlockBody>
                            </div>
                            <div className="flex flex-col">
                                <GeneralBlockHeader variant="dashboard">
                                    <GeneralBlockTitle>
                                        <FormattedMessage {...membershipMessages.allowedParkings} />
                                    </GeneralBlockTitle>
                                </GeneralBlockHeader>
                                <GeneralBlockBody variant="dashboard">
                                    {isLoadingMembershipGroup || isLoadingSubParkings ? (
                                        <Skeleton className="h-8 w-full max-w-64" />
                                    ) : (
                                        <GeneralBlockValue size="small">
                                            {containsAllSubParkings ? (
                                                <FormattedMessage {...membershipMessages.allSubParkings} />
                                            ) : (
                                                membershipGroup?.allowed_parkings
                                                    .map((subParkingId) => {
                                                        const foundSubParkings = subParkings?.results.find(
                                                            (subParking) => subParking.id === subParkingId,
                                                        );
                                                        const isAllSubParking = subParkingId === ALL_POINTS_OPTION;
                                                        if (isAllSubParking) {
                                                            return formatMessage(membershipMessages.allSubParkings);
                                                        }
                                                        return foundSubParkings?.name || subParkingId;
                                                    })
                                                    .join(', ')
                                            )}
                                        </GeneralBlockValue>
                                    )}
                                </GeneralBlockBody>
                            </div>
                            <div className="relative z-20 flex flex-col">
                                <GeneralBlockHeader variant="dashboard">
                                    <GeneralBlockTitle>
                                        <FormattedMessage {...membershipMessages.allowedPoints} />
                                    </GeneralBlockTitle>
                                </GeneralBlockHeader>
                                <GeneralBlockBody variant="dashboard">
                                    {isLoadingMembershipGroup || isLoadingPoints ? (
                                        <Skeleton className="h-8 w-full max-w-64" />
                                    ) : (
                                        <GeneralBlockValue size="small">
                                            {containsAllPoints ? (
                                                <FormattedMessage {...membershipMessages.allPoints} />
                                            ) : (
                                                membershipGroup?.allowed_points
                                                    .map((pointId) => {
                                                        const foundPoint = points?.results.find(
                                                            (point) => point.id === pointId,
                                                        );
                                                        const isAllPoint = pointId === ALL_POINTS_OPTION;
                                                        if (isAllPoint) {
                                                            return formatMessage(membershipMessages.allPoints);
                                                        }
                                                        return foundPoint?.name || pointId;
                                                    })
                                                    .join(', ')
                                            )}
                                        </GeneralBlockValue>
                                    )}
                                </GeneralBlockBody>
                            </div>
                            <div className="relative z-20 flex flex-col">
                                <GeneralBlockHeader variant="dashboard">
                                    <GeneralBlockTitle>
                                        <FormattedMessage {...membershipMessages.description} />
                                    </GeneralBlockTitle>
                                </GeneralBlockHeader>
                                <GeneralBlockBody variant="dashboard">
                                    {isLoadingMembershipGroup ? (
                                        <Skeleton className="h-8 w-full max-w-64"/>
                                    ) : (
                                        <GeneralBlockValue size="small">
                                            {membershipGroup?.description ?? '-'}
                                        </GeneralBlockValue>
                                    )}
                                </GeneralBlockBody>
                            </div>
                            <div className="xs:col-span-2 relative z-20 flex flex-col md:col-span-1 lg:col-span-2">
                                <GeneralBlockHeader variant="dashboard">
                                    <GeneralBlockTitle>
                                        <FormattedMessage {...membershipMessages.allowedDays} />
                                    </GeneralBlockTitle>
                                </GeneralBlockHeader>
                                <GeneralBlockBody variant="dashboard" className="flex-row justify-start gap-2">
                                    {isLoadingMembershipGroup ? (
                                        <>
                                            <Skeleton className="h-8 w-full max-w-16" />
                                            <Skeleton className="h-8 w-full max-w-16" />
                                            <Skeleton className="h-8 w-full max-w-16" />
                                            <Skeleton className="h-8 w-full max-w-16" />
                                            <Skeleton className="h-8 w-full max-w-16" />
                                            <Skeleton className="h-8 w-full max-w-16" />
                                            <Skeleton className="h-8 w-full max-w-16" />
                                        </>
                                    ) : (
                                        Object.keys(ALLOWED_DAYS).map((day) => (
                                            <Badge
                                                variant="tag"
                                                className={cn(
                                                    'flex h-8 items-center justify-center bg-white p-2 md:text-sm',
                                                )}
                                                key={`${membershipGroup?.id}-${day}`}
                                            >
                                                <span
                                                    className={cn(
                                                        'max-w-[3ch] overflow-hidden opacity-30 md:max-w-none',
                                                        ((membershipGroup?.allowed_days as string[]) || []).includes(
                                                            day,
                                                        ) && 'font-semibold opacity-100',
                                                    )}
                                                >
                                                    {formatMessage(
                                                        generalMessagesDays[day as keyof typeof generalMessagesDays],
                                                    )}
                                                </span>
                                            </Badge>
                                        ))
                                    )}
                                </GeneralBlockBody>
                            </div>
                            <img
                                src={car}
                                alt="Car"
                                className="absolute -right-12 bottom-0 z-10 w-48 max-w-96 md:w-[20vw]"
                            />
                        </SectionBlockBody>
                    </GeneralBlock>
                </SectionBlock>

                <SectionBlock className="md:max-w-sm">
                    <SectionBlockHeader>
                        <SectionBlockHead>
                            <SectionBlockTitle>
                                <FormattedMessage {...membershipMessages.members} />
                            </SectionBlockTitle>
                        </SectionBlockHead>
                        {canSeeChangeMemberButtons && membershipGroup && (
                            <MembershipGroupMemberCreateFormModal
                                onSuccess={handleMembershipGroupUpdate}
                                parkingId={parkingId}
                                membershipGroupId={membershipGroup.id}
                            />
                        )}
                    </SectionBlockHeader>
                    <SectionBlockSeparator />
                    <div className="relative size-full overflow-auto">
                        <SectionBlockBody className="flex w-full flex-col overflow-auto sm:gap-3 md:absolute">
                            {(isLoadingMembershipGroup || isLoadingMembershipGroupMembers) && (
                                <>
                                    <Skeleton className="h-18.5" />
                                    <Skeleton className="h-18.5" />
                                    <Skeleton className="h-18.5" />
                                    <Skeleton className="h-18.5" />
                                </>
                            )}
                            {membershipGroupMembers?.count == 0 && (
                                <GeneralMessageBlock
                                    title={formatMessage(membershipMessages.noMembers)}
                                    subtitle={formatMessage(membershipMessages.noMembersDescription)}
                                    icon={faUsers}
                                    className="py-20"
                                />
                            )}
                            {membershipGroupMembers?.results.map((member) => (
                                <MembershipGroupMemberBlock
                                    key={member.id}
                                    member={member}
                                    canSeeChangeMemberButtons={canSeeChangeMemberButtons}
                                    setSelectedMembershipGroupMember={openMemberModal}
                                    setSelectedDeleteMembershipGroupMember={setSelectedDeleteMembershipGroupMember}
                                />
                            ))}
                        </SectionBlockBody>
                    </div>
                </SectionBlock>
            </div>

            <MembershipVehicleSection
                membershipGroupCards={membershipGroupCards}
                membershipGroup={membershipGroup}
                canSeeChangeCardButtons={canSeeChangeCardButtons}
                isLoadingMembershipGroupData={isLoadingMembershipGroup || isLoadingMembershipGroupCards}
                pagination={pagination}
                refetchMembershipGroupCards={refetchMembershipGroupCards}
                handleMembershipGroupUpdate={handleMembershipGroupUpdate}
            />
        </>
    );
};
export { MembershipGroupDashboard };
